import React, { useState, useEffect, useCallback } from 'react';
import { FaFileInvoiceDollar, FaPlus, FaEdit, FaTrash, FaCheckCircle, FaExclamationCircle } from 'react-icons/fa';
import Swal from 'sweetalert2';
import patentesService from '../services/patentesService';
import YearSelector from '../components/common/YearSelector';
import useYearSelector from '../hooks/useYearSelector';
import '../styles/pages/PatentesPage.css';

const SEMESTRES = [
    { value: 1, label: 'Semestre 1', meses: 'Ene – Jun' },
    { value: 2, label: 'Semestre 2', meses: 'Jul – Dic' }
];

const formatCLP = (monto) =>
    new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP', maximumFractionDigits: 0 }).format(monto);

const formatFecha = (fechaStr) => {
    if (!fechaStr) return '-';
    const d = new Date(fechaStr);
    return d.toLocaleDateString('es-CL', { timeZone: 'UTC' });
};

const FORM_INICIAL = {
    id_arriendo: null,
    direccion: '',
    semestre: 1,
    monto: '',
    fecha_pago: '',
    observaciones: ''
};

const PatentesPage = ({ token, id_company }) => {
    const { selectedYear, setSelectedYear, yearOptions } = useYearSelector(null, 5, 1);

    const [arriendos, setArriendos] = useState([]);
    const [pagos, setPagos] = useState([]);
    const [loading, setLoading] = useState(true);

    // Modal pago
    const [showModal, setShowModal] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [formData, setFormData] = useState(FORM_INICIAL);
    const [esPropia, setEsPropia] = useState(false); // propiedad propia (sin arriendo)

    const cargarDatos = useCallback(async () => {
        setLoading(true);
        try {
            const data = await patentesService.getPatentesPorAnio(selectedYear, id_company, token);
            setArriendos(data.arriendos || []);
            setPagos(data.pagos || []);
        } catch (error) {
            Swal.fire('Error', error.message, 'error');
        } finally {
            setLoading(false);
        }
    }, [selectedYear, id_company, token]);

    useEffect(() => {
        cargarDatos();
    }, [cargarDatos]);

    // -------------------------------------------------------
    // Helpers para encontrar pagos
    // -------------------------------------------------------
    const getPagoArriendo = (id_arriendo, semestre) =>
        pagos.find(p => p.id_arriendo === id_arriendo && p.semestre === semestre) || null;

    // Pagos de propiedades propias (sin arriendo asociado), agrupados por dirección
    const pagosPropiedad = pagos.filter(p => p.id_arriendo === null);
    const propiasPorDireccion = pagosPropiedad.reduce((acc, pago) => {
        if (!acc[pago.direccion]) acc[pago.direccion] = {};
        acc[pago.direccion][pago.semestre] = pago;
        return acc;
    }, {});

    // -------------------------------------------------------
    // Abrir modal
    // -------------------------------------------------------
    const abrirModalNuevo = (arriendo = null, semestre = 1) => {
        setEditingId(null);
        setEsPropia(!arriendo);
        setFormData({
            ...FORM_INICIAL,
            id_arriendo: arriendo ? arriendo.id_arriendo : null,
            direccion: arriendo ? arriendo.direccion : '',
            semestre,
            anio: selectedYear
        });
        setShowModal(true);
    };

    const abrirModalEditar = (pago) => {
        setEditingId(pago.id_patente_pago);
        setEsPropia(!pago.id_arriendo);
        setFormData({
            id_arriendo: pago.id_arriendo,
            direccion: pago.direccion,
            semestre: pago.semestre,
            anio: pago.anio,
            monto: pago.monto,
            fecha_pago: pago.fecha_pago ? pago.fecha_pago.substring(0, 10) : '',
            observaciones: pago.observaciones || ''
        });
        setShowModal(true);
    };

    const cerrarModal = () => {
        setShowModal(false);
        setEditingId(null);
        setFormData(FORM_INICIAL);
    };

    // -------------------------------------------------------
    // Guardar pago
    // -------------------------------------------------------
    const guardarPago = async () => {
        const { direccion, semestre, monto, fecha_pago } = formData;
        if (!direccion || !semestre || !monto || !fecha_pago) {
            Swal.fire('Campos requeridos', 'Completa dirección, semestre, monto y fecha de pago.', 'warning');
            return;
        }

        const payload = {
            id_company,
            id_arriendo: formData.id_arriendo || null,
            direccion,
            semestre: Number(semestre),
            anio: selectedYear,
            monto: Number(monto),
            fecha_pago,
            observaciones: formData.observaciones || null
        };

        try {
            if (editingId) {
                await patentesService.updatePago(editingId, payload, token);
            } else {
                await patentesService.createPago(payload, token);
            }
            cerrarModal();
            await cargarDatos();
        } catch (error) {
            Swal.fire('Error', error.message, 'error');
        }
    };

    // -------------------------------------------------------
    // Eliminar pago
    // -------------------------------------------------------
    const eliminarPago = async (pago) => {
        const result = await Swal.fire({
            title: '¿Eliminar pago?',
            text: `${pago.direccion} — Semestre ${pago.semestre} ${pago.anio}`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Eliminar',
            cancelButtonText: 'Cancelar',
            confirmButtonColor: '#e53935'
        });
        if (!result.isConfirmed) return;

        try {
            await patentesService.deletePago(pago.id_patente_pago, id_company, token);
            await cargarDatos();
        } catch (error) {
            Swal.fire('Error', error.message, 'error');
        }
    };

    // -------------------------------------------------------
    // Celda de semestre para un arriendo
    // -------------------------------------------------------
    const CeldaSemestre = ({ arriendo, semestre }) => {
        const pago = getPagoArriendo(arriendo.id_arriendo, semestre);
        if (!pago) {
            return (
                <td className="patentes-celda patentes-celda--vacia">
                    <button
                        className="patentes-btn-agregar-celda"
                        title="Registrar pago"
                        onClick={() => abrirModalNuevo(arriendo, semestre)}
                    >
                        <FaPlus /> Registrar
                    </button>
                </td>
            );
        }
        return (
            <td className="patentes-celda patentes-celda--pagado">
                <div className="patentes-celda-info">
                    <span className={`patentes-badge ${pago.registrado ? 'patentes-badge--contabilizado' : 'patentes-badge--pendiente'}`}>
                        {pago.registrado ? <><FaCheckCircle /> Contabilizado</> : <><FaExclamationCircle /> Sin contabilizar</>}
                    </span>
                    <span className="patentes-monto">{formatCLP(pago.monto)}</span>
                    <span className="patentes-fecha">{formatFecha(pago.fecha_pago)}</span>
                </div>
                <div className="patentes-celda-acciones">
                    <button className="patentes-btn-icon" title="Editar" onClick={() => abrirModalEditar(pago)}>
                        <FaEdit />
                    </button>
                    <button className="patentes-btn-icon patentes-btn-icon--danger" title="Eliminar" onClick={() => eliminarPago(pago)}>
                        <FaTrash />
                    </button>
                </div>
            </td>
        );
    };

    // -------------------------------------------------------
    // Render
    // -------------------------------------------------------
    return (
        <div className="patentes-page">
            {/* Header */}
            <div className="patentes-header">
                <div className="patentes-header-left">
                    <FaFileInvoiceDollar className="patentes-page-icon" />
                    <div>
                        <h1>Patentes Municipales</h1>
                        <p>Registro de pagos de patentes por inmueble y semestre</p>
                    </div>
                </div>
                <div className="patentes-header-right">
                    <YearSelector
                        selectedYear={selectedYear}
                        onYearChange={setSelectedYear}
                        yearOptions={yearOptions}
                    />
                    <button className="patentes-btn-primary" onClick={() => abrirModalNuevo()}>
                        <FaPlus /> Agregar pago de patente
                    </button>
                </div>
            </div>

            {/* Tabla */}
            {loading ? (
                <div className="patentes-loading">Cargando...</div>
            ) : (
                <div className="patentes-tabla-wrapper">
                    <table className="patentes-tabla">
                        <thead>
                            <tr>
                                <th className="patentes-th-direccion">Dirección</th>
                                {SEMESTRES.map(s => (
                                    <th key={s.value} className="patentes-th-semestre">
                                        {s.label}
                                        <span className="patentes-th-meses">{s.meses}</span>
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {/* Filas de arriendos vigentes */}
                            {arriendos.length === 0 && pagosPropiedad.length === 0 && (
                                <tr>
                                    <td colSpan={3} className="patentes-empty">
                                        No hay arriendos vigentes ni pagos propios en {selectedYear}.
                                    </td>
                                </tr>
                            )}
                            {arriendos.map(arriendo => (
                                <tr key={`arr-${arriendo.id_arriendo}`}>
                                    <td className="patentes-td-direccion">
                                        <span className="patentes-direccion">{arriendo.direccion}</span>
                                        {arriendo.comuna && (
                                            <span className="patentes-comuna">{arriendo.comuna}</span>
                                        )}
                                    </td>
                                    {SEMESTRES.map(s => (
                                        <CeldaSemestre
                                            key={s.value}
                                            arriendo={arriendo}
                                            semestre={s.value}
                                        />
                                    ))}
                                </tr>
                            ))}
                            {/* Filas de propiedades propias */}
                            {Object.entries(propiasPorDireccion).map(([direccion, semestreMap]) => (
                                <tr key={`propio-${direccion}`} className="patentes-fila-propia">
                                    <td className="patentes-td-direccion">
                                        <span className="patentes-direccion">{direccion}</span>
                                        <span className="patentes-tag-propia">Propiedad propia</span>
                                    </td>
                                    {SEMESTRES.map(s => {
                                        const pago = semestreMap[s.value];
                                        if (!pago) {
                                            return (
                                                <td key={s.value} className="patentes-celda patentes-celda--vacia">
                                                    <button
                                                        className="patentes-btn-agregar-celda"
                                                        title="Registrar pago"
                                                        onClick={() => {
                                                            setFormData({ ...FORM_INICIAL, direccion, semestre: s.value, anio: selectedYear });
                                                            setEsPropia(true);
                                                            setEditingId(null);
                                                            setShowModal(true);
                                                        }}
                                                    >
                                                        <FaPlus /> Registrar
                                                    </button>
                                                </td>
                                            );
                                        }
                                        return (
                                            <td key={s.value} className="patentes-celda patentes-celda--pagado">
                                                <div className="patentes-celda-info">
                                                    <span className={`patentes-badge ${pago.registrado ? 'patentes-badge--contabilizado' : 'patentes-badge--pendiente'}`}>
                                                        {pago.registrado ? <><FaCheckCircle /> Contabilizado</> : <><FaExclamationCircle /> Sin contabilizar</>}
                                                    </span>
                                                    <span className="patentes-monto">{formatCLP(pago.monto)}</span>
                                                    <span className="patentes-fecha">{formatFecha(pago.fecha_pago)}</span>
                                                </div>
                                                <div className="patentes-celda-acciones">
                                                    <button className="patentes-btn-icon" title="Editar" onClick={() => abrirModalEditar(pago)}>
                                                        <FaEdit />
                                                    </button>
                                                    <button className="patentes-btn-icon patentes-btn-icon--danger" title="Eliminar" onClick={() => eliminarPago(pago)}>
                                                        <FaTrash />
                                                    </button>
                                                </div>
                                            </td>
                                        );
                                    })}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Modal pago */}
            {showModal && (
                <div className="patentes-modal-overlay" onClick={cerrarModal}>
                    <div className="patentes-modal" onClick={e => e.stopPropagation()}>
                        <div className="patentes-modal-header">
                            <h2>{editingId ? 'Editar pago de patente' : 'Registrar pago de patente'}</h2>
                            <button className="patentes-modal-close" onClick={cerrarModal}>×</button>
                        </div>
                        <div className="patentes-modal-body">
                            <div className="patentes-form-grid">
                                <div className="patentes-form-group patentes-form-group--full">
                                    <label>Dirección *</label>
                                    <input
                                        type="text"
                                        value={formData.direccion}
                                        onChange={e => setFormData({ ...formData, direccion: e.target.value })}
                                        placeholder="Ej: Av. Principal 123, Santiago"
                                        disabled={!!formData.id_arriendo}
                                    />
                                </div>
                                <div className="patentes-form-group">
                                    <label>Semestre *</label>
                                    <select
                                        value={formData.semestre}
                                        onChange={e => setFormData({ ...formData, semestre: Number(e.target.value) })}
                                        disabled={!!formData.id_arriendo && !esPropia && !editingId}
                                    >
                                        <option value={1}>Semestre 1 (Ene – Jun)</option>
                                        <option value={2}>Semestre 2 (Jul – Dic)</option>
                                    </select>
                                </div>
                                <div className="patentes-form-group">
                                    <label>Monto pagado *</label>
                                    <input
                                        type="text"
                                        inputMode="numeric"
                                        value={formData.monto === '' ? '' : Number(formData.monto).toLocaleString('es-CL')}
                                        onChange={e => setFormData({ ...formData, monto: e.target.value.replace(/\D/g, '') })}
                                        placeholder="0"
                                    />
                                </div>
                                <div className="patentes-form-group">
                                    <label>Fecha de pago *</label>
                                    <input
                                        type="date"
                                        value={formData.fecha_pago}
                                        onChange={e => setFormData({ ...formData, fecha_pago: e.target.value })}
                                    />
                                </div>
                                <div className="patentes-form-group patentes-form-group--full">
                                    <label>Observaciones</label>
                                    <textarea
                                        value={formData.observaciones}
                                        onChange={e => setFormData({ ...formData, observaciones: e.target.value })}
                                        rows={3}
                                        placeholder="Notas adicionales (opcional)"
                                    />
                                </div>
                            </div>
                        </div>
                        <div className="patentes-modal-footer">
                            <button className="patentes-btn-secondary" onClick={cerrarModal}>Cancelar</button>
                            <button className="patentes-btn-primary" onClick={guardarPago}>
                                {editingId ? 'Guardar cambios' : 'Registrar pago'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PatentesPage;
